import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Printer, ExternalLink, Download, Mail, Send, Loader2, Eye, History } from "lucide-react";
import CommunicationHistory from "@/components/CommunicationHistory";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCY_SYMBOLS = { EUR: "€", GBP: "£", USD: "$", AED: "د.إ" };

const PAYMENT_METHOD_LABELS = {
  bank_transfer: "Bank Transfer",
  card:          "Card",
  cash:          "Cash",
  tikkie:        "Tikkie",
  other:         "Other",
};

function fmt(amount, currency = "EUR") {
  const sym = CURRENCY_SYMBOLS[currency] || currency;
  return `${sym}${Number(amount || 0).toFixed(2)}`;
}

export default function ReceiptDetail() {
  const { receiptId } = useParams();
  const navigate      = useNavigate();
  const { getAuthHeaders } = useAuth();

  const [loading, setLoading] = useState(true);
  const [receipt, setReceipt] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [previewingPdf,  setPreviewingPdf]  = useState(false);
  const [pdfVersions,    setPdfVersions]    = useState([]);
  const [sendingEmail,   setSendingEmail]   = useState(null); // "test" | "customer"
  const [emailOverride,  setEmailOverride]  = useState("");

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/receipts/${receiptId}`, { headers: getAuthHeaders() })
      .then(r => {
        setReceipt(r.data);
        setEmailOverride(r.data.customer_email || "");
      })
      .catch(() => { toast.error("Receipt not found"); navigate("/receipts"); })
      .finally(() => setLoading(false));
    axios.get(`${API}/receipts/${receiptId}/pdf-versions`, { headers: getAuthHeaders() })
      .then(r => setPdfVersions(r.data || [])).catch(() => {});
  }, [receiptId]);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await axios.post(`${API}/receipts/${receiptId}/pdf`, {}, {
        headers: { ...getAuthHeaders(), Accept: "application/pdf" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${receipt?.receipt_number || "receipt"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      axios.get(`${API}/receipts/${receiptId}/pdf-versions`, { headers: getAuthHeaders() })
        .then(r => setPdfVersions(r.data || [])).catch(() => {});
    } catch {
      toast.error("PDF generation failed");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePreviewPdf = async () => {
    setPreviewingPdf(true);
    try {
      const res = await axios.post(`${API}/receipts/${receiptId}/pdf`, {}, {
        headers: { ...getAuthHeaders(), Accept: "application/pdf" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      axios.get(`${API}/receipts/${receiptId}/pdf-versions`, { headers: getAuthHeaders() })
        .then(r => setPdfVersions(r.data || [])).catch(() => {});
    } catch {
      toast.error("PDF preview failed");
    } finally {
      setPreviewingPdf(false);
    }
  };

  const handleSendEmail = async (isTest) => {
    setSendingEmail(isTest ? "test" : "customer");
    try {
      const res = await axios.post(
        `${API}/receipts/${receiptId}/send-email`,
        { is_test: isTest, recipient_email: emailOverride || undefined },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message);
      if (!isTest) {
        setReceipt(r => r ? { ...r, receipt_sent_at: new Date().toISOString(), receipt_sent_to: emailOverride || r.customer_email } : r);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email failed");
    } finally {
      setSendingEmail(null);
    }
  };

  if (loading) return (
    <DashboardLayout>
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    </DashboardLayout>
  );

  if (!receipt) return null;

  const sym = CURRENCY_SYMBOLS[receipt.currency] || receipt.currency;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-2xl mx-auto">

        {/* Header controls — hidden when printing */}
        <div className="mb-6 print:hidden space-y-3">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => navigate("/receipts")}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back to Receipts
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm"
                onClick={() => navigate(`/invoices/${receipt.invoice_id}`)}>
                <ExternalLink className="w-4 h-4 mr-2" />View Invoice
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.print()}>
                <Printer className="w-4 h-4 mr-2" />Print
              </Button>
            </div>
          </div>

          {/* PDF / Email action strip */}
          <div className="flex flex-wrap items-center gap-2 p-3 bg-slate-50 rounded-xl border border-slate-200">
            <Button size="sm" variant="outline" className="text-xs h-8"
              onClick={handlePreviewPdf} disabled={previewingPdf || downloadingPdf}>
              {previewingPdf
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <Eye className="w-3.5 h-3.5 mr-1.5" />}
              Preview PDF
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-8"
              onClick={handleDownloadPdf} disabled={downloadingPdf || previewingPdf}>
              {downloadingPdf
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <Download className="w-3.5 h-3.5 mr-1.5" />}
              Download PDF
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-8"
              onClick={() => handleSendEmail(true)} disabled={!!sendingEmail}>
              {sendingEmail === "test"
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <Mail className="w-3.5 h-3.5 mr-1.5" />}
              Test Receipt Email → Admin
            </Button>
            <div className="flex gap-1 flex-1 min-w-[200px]">
              <Input
                value={emailOverride}
                onChange={e => setEmailOverride(e.target.value)}
                placeholder={receipt?.customer_email ? "" : "No email on file — enter manually"}
                className="h-8 text-xs flex-1" />
              <Button size="sm" className="text-xs h-8 bg-purple-700 hover:bg-purple-800 text-white shrink-0"
                onClick={() => handleSendEmail(false)} disabled={!!sendingEmail}>
                {sendingEmail === "customer"
                  ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  : <Send className="w-3.5 h-3.5 mr-1" />}
                Send Receipt to Customer
              </Button>
            </div>
            {receipt?.receipt_sent_at && (
              <p className="text-xs text-green-600 w-full">
                Sent {format(new Date(receipt.receipt_sent_at), "d MMM yyyy")}
                {receipt.receipt_sent_to && ` → ${receipt.receipt_sent_to}`}
              </p>
            )}
          </div>

          {/* PDF version history */}
          {pdfVersions.length > 0 && (
            <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-xl border border-slate-200">
              <History className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-slate-500 mb-1">PDF History</p>
                <div className="space-y-0.5">
                  {pdfVersions.map(v => (
                    <p key={v.version} className="text-xs text-slate-500">
                      v{v.version} · {v.generated_at ? format(new Date(v.generated_at), "d MMM yyyy, HH:mm") : "—"}
                      {v.generated_by && <span className="text-slate-400"> · {v.generated_by}</span>}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Receipt document */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden print:shadow-none print:border-0">

          {/* Document header */}
          <div className="bg-gradient-to-r from-purple-700 to-purple-900 px-8 py-6 text-white">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-purple-200 mb-1">
              The Better Fare
            </p>
            <h1 className="text-2xl font-bold tracking-tight">Payment Receipt</h1>
          </div>

          {/* Receipt meta */}
          <div className="px-8 py-5 border-b border-slate-100 grid grid-cols-2 gap-x-8 gap-y-3">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide">Receipt No.</p>
              <p className="font-mono font-bold text-purple-700 text-lg mt-0.5">
                {receipt.receipt_number}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide">Invoice No.</p>
              <button
                onClick={() => navigate(`/invoices/${receipt.invoice_id}`)}
                className="font-mono font-bold text-blue-600 hover:underline text-lg mt-0.5 block print:text-slate-700 print:no-underline">
                {receipt.invoice_number}
              </button>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide">Payment Date</p>
              <p className="font-medium text-slate-800 mt-0.5">
                {receipt.payment_date
                  ? format(new Date(receipt.payment_date), "d MMMM yyyy")
                  : "—"}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide">Customer</p>
              <p className="font-medium text-slate-800 mt-0.5">{receipt.customer_name || "—"}</p>
            </div>
            {receipt.installment_label && (
              <div className="col-span-2">
                <p className="text-xs text-slate-400 uppercase tracking-wide">Installment</p>
                <p className="font-medium text-slate-700 mt-0.5">{receipt.installment_label}</p>
              </div>
            )}
          </div>

          {/* Payment details */}
          <div className="px-8 py-5 border-b border-slate-100 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Amount Received</span>
              <span className="text-xl font-bold font-mono text-green-700">
                {fmt(receipt.amount_received, receipt.currency)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Payment Method</span>
              <span className="text-sm font-medium text-slate-700">
                {PAYMENT_METHOD_LABELS[receipt.payment_method] || receipt.payment_method || "—"}
              </span>
            </div>
            {receipt.payment_reference && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Reference</span>
                <span className="text-sm font-mono text-slate-600">{receipt.payment_reference}</span>
              </div>
            )}
          </div>

          {/* Balance */}
          <div className="px-8 py-4 border-b border-slate-100 bg-slate-50/50">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700">Remaining Balance</span>
              <span className={`text-lg font-bold font-mono ${Number(receipt.remaining_balance) > 0 ? "text-amber-700" : "text-green-700"}`}>
                {fmt(receipt.remaining_balance, receipt.currency)}
              </span>
            </div>
            {Number(receipt.remaining_balance) === 0 && !receipt.overpaid_amount && (
              <p className="text-xs text-green-600 mt-1 text-right font-medium">
                Invoice fully paid ✓
              </p>
            )}
            {receipt.overpaid_amount > 0 && (
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className="text-amber-700 font-semibold">Overpaid Amount</span>
                <span className="font-mono font-bold text-amber-700">
                  {fmt(receipt.overpaid_amount, receipt.currency)}
                </span>
              </div>
            )}
            {receipt.overpaid_amount > 0 && (
              <p className="text-xs text-amber-600 mt-1 text-right italic">
                Payment exceeds invoice total — please review with customer.
              </p>
            )}
          </div>

          {/* Required statement */}
          <div className="px-8 py-5">
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-5 py-4">
              <p className="text-sm text-slate-600 leading-relaxed italic">
                This confirms payment received against invoice{" "}
                <span className="font-mono font-semibold text-slate-800 not-italic">
                  {receipt.invoice_number}
                </span>
                . This is not a new invoice.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-8 pb-6">
            <p className="text-xs text-slate-400">
              Generated {receipt.created_at
                ? format(new Date(receipt.created_at), "d MMM yyyy, HH:mm")
                : "—"}
            </p>
          </div>
        </div>

      </div>

      {receipt?.id && (
        <div className="max-w-4xl mx-auto px-4 pb-8">
          <CommunicationHistory sourceType="receipt" sourceId={receipt.id} />
        </div>
      )}
    </DashboardLayout>
  );
}
